import { useEffect, useState } from 'react';
import { apiRequest } from '@/lib/api/client';
import { Card, Hero } from './dp120Parts';
import '../dp120.css';
interface RecordItem {
  id: string;
  title: string;
  content?: string;
  description?: string;
  start_time?: string;
  end_time?: string;
  event_type?: string;
  is_completed?: boolean;
  tags?: string;
  is_pinned?: boolean;
}
export function DoctoralSharedService({
  kind,
  onClose,
}: {
  kind: 'notebook' | 'calendar';
  onClose: () => void;
}) {
  const [items, setItems] = useState<RecordItem[]>([]);
  const [draft, setDraft] = useState<Partial<RecordItem>>({ title: '', content: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const base = `/doctoral/services/${kind}`;
  const refresh = () => apiRequest<RecordItem[]>(base, { channel: 'platform' }).then(setItems);
  useEffect(() => {
    void apiRequest<RecordItem[]>(base, { channel: 'platform' })
      .then(setItems)
      .catch((e) => setError(String(e.message)));
  }, [base]);
  const save = async () => {
    setBusy(true);
    try {
      const body =
        kind === 'notebook'
          ? {
              title: draft.title,
              content: draft.content || '',
              tags: draft.tags || null,
              is_pinned: draft.is_pinned || false,
            }
          : {
              title: draft.title,
              start_time: draft.start_time,
              end_time: draft.end_time || null,
              description: draft.description || '',
              event_type: draft.event_type || 'EVENT',
              is_completed: draft.is_completed || false,
            };
      await apiRequest(`${base}${draft.id ? `/${draft.id}` : ''}`, {
        channel: 'platform',
        method: draft.id ? 'PUT' : 'POST',
        body,
      });
      await refresh();
      setDraft({ title: '', content: '' });
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string) => {
    setBusy(true);
    try {
      await apiRequest(`${base}/${id}`, { channel: 'platform', method: 'DELETE' });
      await refresh();
      if (draft.id === id) setDraft({ title: '', content: '' });
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка удаления');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="dp120-root open">
      <div className="dp120-shell">
        <main className="dp120-main">
          <button className="dp120-back" onClick={onClose}>
            ← Назад
          </button>
          <Hero
            kicker="ACADEMIA"
            title={kind === 'notebook' ? 'Академический блокнот' : 'Академический календарь'}
            text="Личные записи в общем сервисе Академии."
          />
          {error && <p role="alert">{error}</p>}
          <Card title={draft.id ? 'Редактирование' : 'Новая запись'} span="span12">
            <div className="dp120-form">
              <label className="dp120-field">
                <span>Название</span>
                <input
                  value={draft.title || ''}
                  maxLength={250}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                />
              </label>
              {kind === 'calendar' && (
                <>
                  {(['start_time', 'end_time'] as const).map((key) => (
                    <label className="dp120-field" key={key}>
                      <span>{key === 'start_time' ? 'Начало' : 'Окончание'}</span>
                      <input
                        type="datetime-local"
                        value={draft[key] || ''}
                        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                      />
                    </label>
                  ))}
                  <label className="dp120-field">
                    <span>Вид события</span>
                    <select
                      value={draft.event_type || 'EVENT'}
                      onChange={(e) => setDraft({ ...draft, event_type: e.target.value })}
                    >
                      <option value="EVENT">Событие</option>
                      <option value="DEADLINE">Дедлайн</option>
                      <option value="MEETING">Встреча</option>
                    </select>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={draft.is_completed || false}
                      onChange={(e) => setDraft({ ...draft, is_completed: e.target.checked })}
                    />{' '}
                    Завершено
                  </label>
                </>
              )}
              <label className="dp120-field full">
                <span>{kind === 'notebook' ? 'Текст заметки' : 'Описание'}</span>
                <textarea
                  value={kind === 'notebook' ? draft.content || '' : draft.description || ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      [kind === 'notebook' ? 'content' : 'description']: e.target.value,
                    })
                  }
                />
              </label>
              {kind === 'notebook' && (
                <>
                  <label className="dp120-field">
                    <span>Теги</span>
                    <input
                      value={draft.tags || ''}
                      onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
                    />
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={draft.is_pinned || false}
                      onChange={(e) => setDraft({ ...draft, is_pinned: e.target.checked })}
                    />{' '}
                    Закрепить
                  </label>
                </>
              )}
            </div>
            <button
              className="dp120-mini mint"
              disabled={busy || !draft.title?.trim()}
              onClick={() => void save()}
            >
              Сохранить
            </button>
            {draft.id && (
              <button className="dp120-mini" onClick={() => setDraft({ title: '', content: '' })}>
                Отмена
              </button>
            )}
          </Card>
          {!items.length && <p>Записей пока нет.</p>}
          {items.map((item) => (
            <Card key={item.id} title={item.title} sub={item.start_time} span="span12">
              <p style={{ whiteSpace: 'pre-wrap' }}>{item.content || item.description}</p>
              {item.is_completed && <span>Завершено</span>}
              <div className="dp120-row-actions">
                <button className="dp120-mini" onClick={() => setDraft({ ...item })}>
                  Изменить
                </button>
                <button className="dp120-mini" disabled={busy} onClick={() => void remove(item.id)}>
                  Удалить
                </button>
              </div>
            </Card>
          ))}
        </main>
      </div>
    </div>
  );
}
